import { Avatar, Box, IconButton, Skeleton, Typography } from "@mui/material";
import { UserType } from "@WahooFitness/cloud-client-types";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useUserContext,
  useNativeMessagingContext,
  postChangeAvatar,
  usePubSubContext,
  useSnackbarContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import WfIconContentUserRegular from "@WahooFitness/redesignr/WfIcons/Generated/WfIconContentUserRegular.js";
import useUserDisplay from "@/hooks/useUserDisplay";

const UserCard = ({ user }: { user: UserType }) => {
  const { enqueueSnackbar } = useSnackbarContext();
  const [avatarUpdating, setAvatarUpdating] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar?.url);
  const errorTimeOut = useRef<NodeJS.Timeout | null>(null);

  const { mutateUser } = useUserContext();
  const { subscribeToUserUpdates } = usePubSubContext();
  const { addEventResponseListener, removeEventResponseListener } = useNativeMessagingContext();

  const failedToChangeAvatar = useCallback(() => {
    enqueueSnackbar({
      message: t`Something went wrong. Please check your network and try again.`,
      severity: "error",
    });
    setAvatarUpdating(false);
  }, [enqueueSnackbar]);

  const clearErrorTimeout = useCallback(() => {
    if (errorTimeOut.current) {
      clearTimeout(errorTimeOut.current);
    }
  }, []);

  useEffect(() => {
    const eventListener = addEventResponseListener("changeAvatar", ({ eventResponseData }) => {
      clearErrorTimeout();
      switch (eventResponseData.status) {
        case "pending":
          // If we don't hear back in 60 seconds, assume it failed
          errorTimeOut.current = setTimeout(failedToChangeAvatar, 60000);
          break;
        case "success":
          // In case we never get the pub/sub, we will reset the display after 10 seconds
          errorTimeOut.current = setTimeout(() => {
            setAvatarUpdating(false);
            mutateUser();
          }, 10000);
          break;
        case "error":
          failedToChangeAvatar();
          break;
        case "cancelled":
          setAvatarUpdating(false);
          break;
        default:
          break;
      }
    });
    return () => {
      removeEventResponseListener(eventListener);
    };
  }, [
    addEventResponseListener,
    clearErrorTimeout,
    failedToChangeAvatar,
    mutateUser,
    removeEventResponseListener,
  ]);

  const refreshUserData = useCallback(
    (_event: string) => {
      mutateUser();
    },
    [mutateUser]
  );

  // This takes down the loading skeleton when the avatar url changes after pub/sub
  useEffect(() => {
    if (user.avatar?.url !== avatarUrl) {
      clearErrorTimeout();
      setAvatarUpdating(false);
      setAvatarUrl(user.avatar?.url);
    }
  }, [avatarUrl, clearErrorTimeout, user.avatar?.url]);

  useEffect(() => {
    const subscription = subscribeToUserUpdates(user.id, refreshUserData);
    return () => {
      subscription.unsubscribe();
    };
  }, [subscribeToUserUpdates, user.id, refreshUserData]);

  const handleImageRequest = useCallback(() => {
    clearErrorTimeout();
    postChangeAvatar();
    setAvatarUpdating(true);
  }, [clearErrorTimeout]);

  const { userDisplayName, userInitials } = useUserDisplay(user);

  return (
    <Box display="flex" flexDirection="column" textAlign={"center"} gap={2} alignItems="center">
      {avatarUpdating ? (
        <Skeleton height={44} width={44} variant="circular" />
      ) : (
        <IconButton onClick={handleImageRequest} size="large" data-testid="profile.avatar">
          <Avatar
            src={user.avatar?.url}
            imgProps={{ height: 48, width: 48 }}
            sx={{ color: "text.primary", bgcolor: "secondary.main", height: 48, width: 48 }}
          >
            <Typography variant="ui-md">
              {userInitials || <WfIconContentUserRegular fontSize="large" />}
            </Typography>
          </Avatar>
        </IconButton>
      )}
      <Box display="flex" flexDirection="column" width="100%" flexShrink={1}>
        <Typography variant="prose-lg-medium" color="text.primary" sx={{ wordBreak: "break-all" }}>
          {userDisplayName}
        </Typography>
        <Typography variant="prose-sm" color="text.secondary" sx={{ wordBreak: "break-all" }}>
          {user.email}
        </Typography>
      </Box>
    </Box>
  );
};

export default UserCard;
